import { BASE_SIZES } from "../constants/modal";

export type Size = { male: number; female: number };

/** The systems BASE_SIZES draws a grid for. */
export type ShoeSizeSystem = "EU" | "US" | "UK";

export interface ShoeSizeRange {
  from?: number;
  to?: number;
  // The grid advances by half sizes, so a brand either sells them (0.5) or
  // sells whole sizes only (1) — no other stride describes a real catalogue.
  step?: 0.5 | 1;
}

/** `{ "EU": { "from": 34, "to": 50.5, "step": 1 } }` — a range per system. */
export type ShoeSizeRanges = Partial<Record<ShoeSizeSystem, ShoeSizeRange>>;

// A bound only means something as a positive size; absent, zero or negative
// leaves that end of the grid alone.
const positive = (value?: number) =>
  typeof value === "number" && value > 0 ? value : undefined;

const filterSizes = (sizes: Size[], min: number, max: number) =>
  sizes.filter((s) => s.male >= min && s.male <= max);

const filterEuSizes = (sizes: Size[], { from, to, step }: ShoeSizeRange) => {
  const bounded = filterSizes(
    sizes,
    positive(from) ?? -Infinity,
    positive(to) ?? Infinity
  );

  // The male and female columns hold the same numbers on the EU grid, so one
  // is enough to decide.
  const kept = bounded.filter(
    ({ male }) => step !== 1 || Number.isInteger(male)
  );

  // A range matching nothing would leave the shopper on an empty grid with no
  // way forward, so a nonsensical config falls back to the untouched list.
  return kept.length ? kept : sizes;
};

/**
 * The shoe grids, with the EU one cut down to the configured range: `from` and
 * `to` bound it in EU sizes (inclusive) and `step` says which sizes lie
 * between them.
 *
 * Only the EU entry is read. A half size is a size a brand may simply not make
 * there, while in US and UK it is the ordinary numbering — EU 41 is UK 7.5 — so
 * narrowing those grids is a different need, and nobody has it yet.
 */
export const shoeSizesWithinRanges = (ranges: ShoeSizeRanges = {}) =>
  BASE_SIZES.map((group) =>
    group.name === "EU" && ranges.EU
      ? { ...group, sizes: filterEuSizes(group.sizes, ranges.EU) }
      : group
  );
